import hyperdom from 'hyperdom'
import pluralize from 'pluralize'
import formatDate from '../../../../../../packages/dates/formatDate.js'
import formatRelativePrice from '../../../packages/ui-helpers/formatRelativePrice.js'
import formats from '../../../../../../packages/dates/formats.js'
import RoomOptions from '../../../roomOptions.jsx'
import roomTypes from '../../../../../../packages/accommodation/roomTypes.js'
import * as gtmDataLayer from '../../../analytics/gtm/dataLayer.js'
import {Message, MessageTypes} from '../../../message.jsx'
import RoomGraphics from '../../../components/roomGraphics.jsx'

export default class ExtraStays {
  constructor (options) {
    this.departure = options.departure
    this.quoteCriteria = options.quoteCriteria
    this.config = options.quoteCriteria.config
    this.modals = options.modals
    this.hotelsBeforeExpanded = false
    this.hotelsAfterExpanded = false
    this.flights = options.flights
    this.promisedFlights = options.promisedFlights
  }

  onadd () {
    gtmDataLayer.activateOptimize()
  }

  onupdate () {
    gtmDataLayer.activateOptimize()
  }

  resetExtraStays (nightsBeforeNumber, nightsAfterNumber) {
    this.quoteCriteria.clearHotelsBefore()
    this.quoteCriteria.clearHotelsAfter()

    if (nightsBeforeNumber > 0) {
      this.setAccommodation('pre', nightsBeforeNumber)
    }

    if (nightsAfterNumber > 0) {
      this.setAccommodation('post', nightsAfterNumber)
    }
  }

  setAccommodation (preOrPost, nightsNumber){
    if (preOrPost !== 'pre' && preOrPost !== 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }

    const hotels = preOrPost === 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
    const areHotelsAvailable = hotels.hasAvailability()
    if (areHotelsAvailable && hotels.nights.length >= nightsNumber) {
      hotels.selectedNights = hotels.nights.find(night => night.number === nightsNumber)
      this.nightsChanged(preOrPost)
    } else {
      this.showHotelsRemovedWarning = true
    }
  }

  beforeLocation () {
    return this.departure.hotels.beforeTour[1]
  }

  afterLocation () {
    return this.departure.hotels.afterTour[0]
  }

  renderHotelsRemovedWarning () {
    if (this.showHotelsRemovedWarning) {
      return <div class='hotels-removed-warning'>
        {new Message({
          header: 'Hotels removed',
          message: 'As you have added or removed people from your party you will need to add hotels before and after the tour again.',
          type: MessageTypes.WARNING,
          onClose: () => this.hideHotelsRemovedWarning(),
        })}
      </div>
    }
  }

  hideHotelsRemovedWarning () {
    this.showHotelsRemovedWarning = false
  }

  renderFlightsRemovedForExtraNightsWarning () {
    return this.flights.renderFlightsRemovedForExtraNightsWarning()
  }

  adjustPromisedFlightsDates () {
    const numberOfNight = this.lastChange === 'pre' ?
      this.quoteCriteria.hotelsBefore.selectedNights :
      this.quoteCriteria.hotelsAfter.selectedNights
    this.promisedFlights.adjustPromisedFlightsDates(this.lastChange, numberOfNight.number)

    if (this.promisedFlights.promisedFlights.searchMode !== 'criteria' && numberOfNight.number > 0) {
      return this.promisedFlights.renderExtraNightsWarning
    }
  }

  nightsChanged (preOrPost) {
    this.lastChange = preOrPost
    const hotel = preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
    hotel.chosenHotel.hotelId = undefined
    hotel.selectedNights.chosenHotel.hotelId = undefined
    this.quoteCriteria.extraNightsChanged()
  }

  selectRooms (hotel) {
    this.roomOptions = new RoomOptions({
      quoteCriteria: {
        party: this.quoteCriteria.party,
        roomAllocations: hotel.roomAllocations(),
      },
      currency: this.departure.prices.currency,
      binding: {
        get: () => hotel.selectedRoomAllocation(),
        set: (room) => {
          hotel.selectRoomAllocation(room)
          this.modals.close()
        },
      },
      config: this.config,
    })

    this.modals.show({
      animationDuration: 0,
      render: () => this.renderRoomOptionsModal(),
      close: () => {
        delete this.roomOptions
      },
      classNames: 'extra-stays__modal',
    })
  }

  renderRoomOptionsModal () {
    return this.roomOptions ? <div class='extrastay_rooms'>
      { this.roomOptions }
    </div> : undefined
  }

  preOrPostHotels (preOrPost) {
    if (this.promisedFlights.promisedFlights.searchMode === 'criteria'){
      this.promisedFlights.hotelsBeforeSelectedNights = this.quoteCriteria.hotelsBefore.selectedNights.number
      this.promisedFlights.hotelsAfterSelectedNights = this.quoteCriteria.hotelsAfter.selectedNights.number
    }
    return preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
  }

  renderHotel (hotel, preOrPost) {
    if (preOrPost != 'pre' && preOrPost != 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }

    const radioButtonGroupName = preOrPost == 'pre' ? 'hotel-before-tour' : 'hotel-after-tour'
    const hotels = this.preOrPostHotels(preOrPost)
    const nights = hotels.selectedNights
    const selectedRoomAllocation = hotel.selectedRoomAllocation()

    const isSelected = hotel == nights.selectedHotel()
    const selectedRoomAllocationPrice = nights.chosenHotel.hotelId
      ? nights.hotels.find(h => h.id === nights.chosenHotel.hotelId).selectedRoomAllocation().price
      : nights.hotels[0].selectedRoomAllocation().price

    const priceToDisplay = isSelected ? selectedRoomAllocation.price : (selectedRoomAllocationPrice - selectedRoomAllocation.price)

    return <li class='pre-post-hotels__item'>
      <label class='o-choice-item -reverse -fullWidth' >
        <input type='radio' class='o-choice-item__radio'
          name={radioButtonGroupName}
          binding={{
            get: () => nights.selectedHotel(),
            set: (hotel) => nights.selectHotel(hotel),
          }}
          value={hotel} />
        <div class='o-choice-item__content'>
          <div class='pre-post-hotel__details'>
            <div class='pre-post-hotel__name'>{ hotel.name }</div>
            <div className='pre-post-hotel__rooms'>
              { this.renderRoomGraphics(selectedRoomAllocation) }
            </div>
            <div class={['pre-post-hotel__price',{'change-button': isSelected ,'plus': !isSelected && priceToDisplay < 0, 'minus': !isSelected && priceToDisplay > 0}]}>{
              !isSelected ? formatRelativePrice({
                currency: this.departure.prices.currency,
                amount: priceToDisplay,
                noPrefixSymbol: isSelected,
              }) : undefined
            }</div>
          </div>
        </div>
      </label>
    </li>
  }

  renderRoomGraphics (selectedRoomAllocation) {
    return selectedRoomAllocation.rooms.map(room => {
      const roomsToDisplay = `${room.count} x ${roomTypes.displayNameForName(room.roomType)}`
      const roomGraphics = new RoomGraphics({room, content: roomsToDisplay})
      return roomGraphics
    })
  }

  renderLocation (location) {
    return location ? `${location.city}, ${location.country}` : ''
  }

  renderLowestPricePerNight (hotel) {
    const chosenHotelId = hotel.chosenHotel.hotelId
    const chosenNights = hotel.selectedNights.number

    const price = (chosenHotelId !== undefined & chosenNights > 0)
      ? hotel.nights[chosenNights].hotels.find(h => h.id === chosenHotelId)._roomAllocations[0].price
      : chosenNights > 0
        ? hotel.nights[chosenNights].hotels[0]._roomAllocations[0].price
        : hotel.nights[1]
          ? hotel.nights[1].hotels[0]._roomAllocations[0].price
          : undefined

    return chosenNights === 0 ? price : price / chosenNights
  }

  renderChosenHotelPrice (preOrPost) {
    if (preOrPost != 'pre' && preOrPost != 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }
    const hotel = preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter

    return <div>from {formatRelativePrice({
      currency: this.departure.prices.currency,
      amount: this.renderLowestPricePerNight(hotel),
      noPrefixSymbol: true,
    })
    } / night</div>
  }

  renderChosenHotelName (preOrPost) {
    if (preOrPost != 'pre' && preOrPost != 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }
    const hotels = preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
    const chosenHotelId = hotels.chosenHotel.hotelId
    const nights = hotels.selectedNights

    return (chosenHotelId !== undefined & nights.number > 0)
      ? <div>{nights.hotels.find(h => h.id === chosenHotelId).name}</div>
      : hotels.nights[0]
        ? <div>{hotels.nights[1].hotels[0].name}</div>
        : hotels.nights[1]
          ? <div>{hotels.nights[1].hotels[0].name}</div>
          : undefined
  }

  renderChosenHotelCity (preOrPost) {
    if (preOrPost != 'pre' && preOrPost != 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }

    const hotels = preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
    const chosenHotel = hotels.chosenHotel.hotelId
    const nights = hotels.selectedNights
    const defaultId = hotels.nights[1].hotels[0].id

    return chosenHotel !== undefined & nights.number > 0
      ? hotels.hotels.find(h => h.id === chosenHotel).city
      : hotels.nights[0]
        ? hotels.hotels.find(h => h.id === defaultId).city
        : preOrPost == 'pre'
          ? this.beforeLocation().city
          : this.afterLocation().city
  }

  shouldExpandHotelList (preOrPost) {
    if (preOrPost != 'pre' && preOrPost != 'post') {
      throw new Error("preOrPost should be either 'pre' or 'post'")
    }
    const hotel = preOrPost == 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter

    return hotel.selectedNights && hotel.selectedNights.hotels.length > 1
  }

  render () {
    return <div class='hotels'>
      {this.quoteCriteria.hotelsBefore.hasAvailability() || this.quoteCriteria.hotelsAfter.hasAvailability() ? this.renderHotelsHeader() : undefined}
      {this.quoteCriteria.hotelsBefore.hasAvailability() ? this.renderHotelsBefore() : undefined}
      {this.quoteCriteria.hotelsBefore.hasAvailability() && this.quoteCriteria.hotelsAfter.hasAvailability() ? this.renderHotelsDivider() : undefined}
      {this.quoteCriteria.hotelsAfter.hasAvailability() ? this.renderHotelsAfter() : undefined}
    </div>
  }

  renderHotelsHeader () {
    return <div className='hotels__header'>
      <h2>Extend your stay</h2>
      <span>Optional</span>
    </div>
  }

  toggleHotelsBefore () {
    this.hotelsBeforeExpanded = !this.hotelsBeforeExpanded
  }

  toggleHotelsAfter () {
    this.hotelsAfterExpanded = !this.hotelsAfterExpanded
  }

  renderHotelsBefore () {
    return <div data-test={'accommodation-before-hotels'} className={['o-accent-box hotels-tab__section', 'accommodation-before-hotels',{'hotels-tab__section-opened': this.hotelsBeforeExpanded}]} onclick={(ev) => {ev.preventDefault(); this.toggleHotelsBefore()}}>
      <div className='hotels-tab__container'>
        <div>
          <i className={['hotels-tab__icon -new-ui_hotel',{'hotels-tab__icon-selected': this.quoteCriteria.hotelsBefore.selectedNights.number > 0}]}></i>
          <div className='hotels-tab__title u-margin-left-small'>
            <div class="hotels-tab__title_text">{this.renderChosenHotelCity('pre')}: Add accommodation before your trip</div>
            <div className='pre-post-hotel-name'>{this.renderChosenHotelName('pre')}</div>
          </div>
        </div>
        <div>
          <div className='hotels-tab__price '>
            <div className={{'price-green': this.quoteCriteria.hotelsBefore.selectedNights.number > 0}}>{this.quoteCriteria.hotelsBefore.selectedNights.number > 0 ? 'Added '+this.quoteCriteria.hotelsBefore.selectedNights.number+' nights' : 'Not added'}</div>
            {this.renderChosenHotelPrice('pre')}
          </div>
          <div className={['hotels-tab__icon-switch',{'rotate-180' : this.hotelsBeforeExpanded}]}/>
        </div>
      </div>

      <div className={['hotels-tab__expandable',{'show-expanded': this.hotelsBeforeExpanded}]} onclick={(e) => {
        e.stopPropagation()
      }}>
        <div className='hotels-tab__expandable-info'>
          <div>
            <div><span className='-new-ui_checkin'/>  Check in</div>
            <div>{this.quoteCriteria.hotelsBefore.selectedNights.number === 0 ? '-' : formatDate(this.departure.startDate.addDays(-this.quoteCriteria.hotelsBefore.selectedNights.number), formats.humanDayMonth)}</div>
          </div>
          <div>
            <div><span className='-new-ui_checkout'/>  Check out</div>
            <div>{this.quoteCriteria.hotelsBefore.selectedNights.number === 0 ? '-' : formatDate(this.departure.startDate, formats.humanDayMonth)}</div>
          </div>
          <div className='hotels-tab__expandable-info-rooms'>
            {this.renderRoomOptions('pre')}
          </div>
        </div>
        <ul className={['pre-post-hotels',{'hotels-tab__section-opened': this.hotelsBeforeExpanded,'show-expanded': this.shouldExpandHotelList('pre')}]}>
          {
            this.quoteCriteria.hotelsBefore.selectedNights.hotels.map(h => {
              return this.renderHotel(h, 'pre')
            })
          }
        </ul>
        { this.lastChange == 'pre' ? this.renderFlightsRemovedForExtraNightsWarning() : '' }
        { this.lastChange == 'pre' ? this.adjustPromisedFlightsDates() : '' }
        <div className='hotels-tab__expandable-select'>
          <select class='o-form-item__txt-input' binding={{
            set: value => {
              const fieldBeingChanged = 'pre'
              this.quoteCriteria.hotelsBefore.selectedNights = value
              this.nightsChanged(fieldBeingChanged)
            },
            get: () => this.quoteCriteria.hotelsBefore.selectedNights,
          }}>
            {
              this.quoteCriteria.hotelsBefore.nights.map(nights => {
                return <option value={nights}>{pluralize('nights', nights.number, true)}</option>
              })
            }
          </select>
        </div>
      </div>
    </div>
  }

  renderHotelsAfter () {
    return <div data-test={'accommodation-after-hotels'} className={['o-accent-box hotels-tab__section', 'accommodation-after-hotels',{'hotels-tab__section-opened': this.hotelsAfterExpanded}]} onclick={(ev) => {ev.preventDefault(); this.toggleHotelsAfter()}}>
      <div className='hotels-tab__container'>
        <div>
          <i className={['hotels-tab__icon -new-ui_hotel',{'hotels-tab__icon-selected': this.quoteCriteria.hotelsAfter.selectedNights.number > 0}]}></i>
          <div className='hotels-tab__title u-margin-left-small'>
            <div class="hotels-tab__title_text">{this.renderChosenHotelCity('post')}: Add accommodation after your trip</div>
            <div className='pre-post-hotel-name'>{this.renderChosenHotelName('post')}</div>
          </div>
        </div>
        <div>
          <div className='hotels-tab__price'>
            <div className={{'price-green': this.quoteCriteria.hotelsAfter.selectedNights.number > 0}}>{this.quoteCriteria.hotelsAfter.selectedNights.number > 0 ? 'Added '+this.quoteCriteria.hotelsAfter.selectedNights.number+' nights' : 'Not added'}</div>
            {this.renderChosenHotelPrice('post')}
          </div>
          <div className={['hotels-tab__icon-switch',{'rotate-180' : this.hotelsAfterExpanded}]}/>
        </div>
      </div>



      <div className={['hotels-tab__expandable',{'show-expanded': this.hotelsAfterExpanded}]} onclick={(e) => {
        e.stopPropagation()
      }}>
        <div className='hotels-tab__expandable-info'>
          <div>
            <div><span className='-new-ui_checkin'/>  Check in</div>
            <div>{this.quoteCriteria.hotelsAfter.selectedNights.number === 0 ? '-' : formatDate(this.departure.endDate, formats.humanDayMonth)}</div>
          </div>
          <div>
            <div><span className='-new-ui_checkout'/>  Check out</div>
            <div>{this.quoteCriteria.hotelsAfter.selectedNights.number === 0 ? '-' : formatDate(this.departure.endDate.addDays(this.quoteCriteria.hotelsAfter.selectedNights.number), formats.humanDayMonth)}</div>
          </div>
          <div className='hotels-tab__expandable-info-rooms'>
            {this.renderRoomOptions('post')}
          </div>
        </div>
        <ul className={['pre-post-hotels',{'hotels-tab__section-opened': this.hotelsAfterExpanded,'show-expanded': this.shouldExpandHotelList('post')}]}>
          {
            this.quoteCriteria.hotelsAfter.selectedNights.hotels.map(h => {
              return this.renderHotel(h, 'post')
            })
          }
        </ul>
        { this.lastChange == 'post' ? this.renderFlightsRemovedForExtraNightsWarning() : '' }
        { this.lastChange == 'post' ? this.adjustPromisedFlightsDates() : '' }
        <div className='hotels-tab__expandable-select'>
          <select class='o-form-item__txt-input' binding={{
            set: value => {
              const fieldBeingChanged = 'post'
              this.quoteCriteria.hotelsAfter.selectedNights = value
              this.nightsChanged(fieldBeingChanged)
            },
            get: () => this.quoteCriteria.hotelsAfter.selectedNights,
          }}>
            {
              this.quoteCriteria.hotelsAfter.nights.map(nights => {
                return <option value={nights}>{pluralize('nights', nights.number, true)}</option>
              })
            }
          </select>
        </div>
      </div>
    </div>

  }

  renderHotelsDivider () {
    return <div className='hotels-divider__container'>
      <div className='hotels-divider__graphics'></div>
      <div className='hotels-divider__text'>Your trip</div>
    </div>
  }

  renderRoomOptions (preOrPost) {
    const h = preOrPost === 'pre' ? this.quoteCriteria.hotelsBefore : this.quoteCriteria.hotelsAfter
    return [<div><span className='-new-ui_roomtype'/> Room options</div>,
      <div>{
        h.selectedNights.number === 0 ? '-' : h.selectedHotel() !== undefined && h.selectedHotel()._roomAllocations.length > 1
          ? <a href='#' className='pre-post-hotel__change-rooms' onclick={(e) => {
            e.preventDefault()
            this.selectRooms(h.selectedHotel(), preOrPost)
          }}>select</a>
          : '-'
      }</div>]
  }
}
